import { createPendoI18n } from '@pendo/i18n';
import en from '@/locales/en.json';
import { getFileFromCDN } from '@/utils/get-from-cdn.utils';
import i18nDateTimeFormatter from './i18n-datetime-formatter';
import i18nListFormatter from './i18n-list-formatter';
import { getActiveUser, getActiveSubscription } from '@/data/session/session-globals.ts';
import { SUPPORTED_LOCALES, UNANNOUNCED_LOCALES } from '@/../locale.constants.mjs';

import momentTimeZone from 'moment-timezone';
import 'moment/locale/ja';
import 'moment/locale/de';
import 'moment/locale/pt-br';
import 'moment/locale/es';

momentTimeZone.locale('en');
const i18n = createPendoI18n({
    locale: 'en',
    messages: { en: Object.freeze(en) },
});

const { i18n_t } = i18n;
const i18n_locale = i18n.global.locale;

function getBrowserLocale () {
    const regionalLanguage = window.navigator.language;

    if (!regionalLanguage) {
        return false;
    }

    // Only Pendozers should access unannounced non-English locales
    if (!getActiveUser()?.isPendozer && UNANNOUNCED_LOCALES.includes(regionalLanguage)) {
        return 'en';
    }

    const noVariantLocale = regionalLanguage.split('-')[0];

    // we do not care about regional variants of en, es, ja, or de (for now)
    return ['en', 'es', 'ja', 'de'].includes(noVariantLocale) ? noVariantLocale : regionalLanguage;
}

function getLocaleOverride () {
    if (!getActiveUser()?.isPendozer) {
        return null;
    }

    const params = new URLSearchParams(window.location.search);

    return params.get('locale');
}

function refreshLocale () {
    const locale =
        getLocaleOverride() || getActiveUser()?.locale || getActiveSubscription()?.locale || getBrowserLocale() || 'en';

    return setLocale(locale);
}

async function setLocale (locale) {
    if (!locale || locale === i18n.locale) {
        return Promise.resolve();
    }

    momentTimeZone.locale(locale);
    i18nDateTimeFormatter.locale = locale;
    i18nListFormatter.locale = locale;

    const loadLocaleFileFn = (loc) => import(`@/locales/${loc}.json`);

    return i18n.changeLocale({ locale, loadLocaleFileFn });
}

async function loadLocaleOptions () {
    // https://pendo-io.atlassian.net/browse/APP-79945
    // Locale file is not fully functional, so we need to manually add supported locales
    const locales = await getFileFromCDN('/i18n/locales.json');

    // Make sure all locales in SUPPORTED_LOCALES are included
    SUPPORTED_LOCALES.forEach((supportedLocale) => {
        if (!locales.some((locale) => locale.id === supportedLocale)) {
            switch (supportedLocale) {
                case 'en':
                    locales.push({ id: supportedLocale, displayName: 'English' });
                    break;
                case 'ja':
                    locales.push({ id: supportedLocale, displayName: '日本語' });
                    break;
                case 'de':
                    locales.push({ id: supportedLocale, displayName: 'Deutsch' });
                    break;
                case 'pt-BR':
                    locales.push({ id: supportedLocale, displayName: 'Português' });
                    break;
                case 'es':
                    locales.push({ id: supportedLocale, displayName: 'Español' });
                    break;
            }
        }
    });

    const localeOptions = locales.filter((locale) => SUPPORTED_LOCALES.includes(locale.id));

    // This is temporary until we officially support/announce these locales
    if (getActiveUser()?.isPendozer) {
        const unannouncedLocaleDisplayNames = {
            'fr-FR': 'Français',
        };

        UNANNOUNCED_LOCALES.forEach((localeId) => {
            localeOptions.push({
                id: localeId,
                displayName: unannouncedLocaleDisplayNames[localeId],
            });
        });
    }

    return localeOptions;
}

export { i18n, i18n_t, i18n_locale, setLocale, refreshLocale, loadLocaleOptions };
